import {
  ManagedLeadAdvancementConstraints,
  ManagedLeadAdvancementField,
  ManagedLeadField,
} from '../../services/api/managed-leads/managedLeads';
import {
  canonicalManagedLeadAmount,
  managedLeadCurrencyToCanonical,
  managedLeadCurrencyToDisplay,
} from './managedLeadCurrency';
import {
  EMPTY_MANAGED_LEAD_VALUE,
  formatManagedLeadCurrency,
  formatManagedLeadValue,
  getManagedLeadFieldValue,
} from './managedLeadFormatting';

/**
 * What the operator is editing, keyed by semantic field name. Every entry is a
 * string in the shape its input works with, not the shape the wire carries: a
 * currency reads `25.000,50` here and travels as `25000.50`.
 */
export type ManagedLeadAdvancementValues = Record<string, string>;

/** One message per rejected semantic field, ready to sit under its input. */
export type ManagedLeadAdvancementErrors = Record<string, string>;

/** Trimmed plain text is capped here unless the policy says otherwise. */
const DEFAULT_MAX_LENGTH = 255;
/** A monetary value carries at most two decimals unless the policy narrows it. */
const DEFAULT_MAX_DECIMALS = 2;

export const ADVANCEMENT_REQUIRED_MESSAGE = 'Campo obbligatorio';
export const ADVANCEMENT_INVALID_DATE_MESSAGE = 'Inserisci una data valida.';
export const ADVANCEMENT_INVALID_TIME_MESSAGE =
  'Inserisci un orario nel formato HH:mm.';
export const ADVANCEMENT_INVALID_CURRENCY_MESSAGE =
  'Inserisci un importo maggiore di zero, con al massimo due decimali.';
export const ADVANCEMENT_UNSUPPORTED_MESSAGE =
  'Questo aggiornamento richiede un dato che questa versione del backoffice non sa gestire. Aggiorna il backoffice per completarlo.';

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * Reads a `YYYY-MM-DD` value as a local calendar day. The parts are handed to
 * the `Date` constructor one by one on purpose: parsing the string would make
 * it UTC midnight, which is the previous day west of Greenwich and would move
 * the date the operator picked.
 */
export const advancementDateToDate = (value: string): Date | null => {
  const match = DATE_PATTERN.exec(value);
  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  // February 31st parses into March: a rolled-over day was never a real date.
  return date.getDate() === Number(day) ? date : null;
};

/** The inverse, read from the local calendar for the same reason. */
export const advancementDateFromDate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * Italian reading of a canonical date, for the places that show it instead of
 * editing it. A calendar day carries no time zone, so it is re-emitted from the
 * parts it was read from. A value the contract does not recognise is handed
 * back untouched rather than blanked: an unexpected shape has to stay visible.
 */
export const advancementDateToDisplay = (canonical: string) => {
  const date = advancementDateToDate(canonical);
  return date
    ? `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
    : canonical;
};

/**
 * An advancement datum as a detail box shows it, rather than as the form edits
 * it. A key HubSpot has never held renders as the empty marker instead of
 * disappearing: a datum nobody has provided has to read as absent, not leave
 * the box that names it looking incomplete.
 */
export const formatAdvancementDatum = (
  advancementData: Record<string, ManagedLeadField<string>>,
  key: string,
  kind: 'date' | 'currency' | 'text'
) => {
  const field = advancementData[key];
  const value = field ? getManagedLeadFieldValue(field) : null;
  if (value === null || value === undefined || value === '') {
    return EMPTY_MANAGED_LEAD_VALUE;
  }
  if (kind === 'date') return advancementDateToDisplay(String(value));
  if (kind === 'currency') return formatManagedLeadCurrency(value);
  return formatManagedLeadValue(value);
};

/**
 * Everything that varies between one field kind and the next: how a stored
 * value becomes an editable one, how an edited one becomes canonical again, and
 * why a non-empty value is unacceptable. Adding a kind is adding an entry.
 */
interface AdvancementKindAdapter {
  toEditor: (canonical: string) => string;
  toCanonical: (edited: string) => string;
  /** The message the canonical value fails on, or `null` when it holds. */
  reject: (
    canonical: string,
    constraints: ManagedLeadAdvancementConstraints
  ) => string | null;
}

const asTyped = (value: string) => value;

/**
 * The kinds this release can render, and nothing else. A descriptor of any
 * other kind is refused rather than skipped: a required datum the form cannot
 * collect must not be written as if the operator had confirmed it.
 */
const KIND_ADAPTERS = new Map<string, AdvancementKindAdapter>([
  [
    'date',
    {
      toEditor: asTyped,
      toCanonical: asTyped,
      reject: (canonical) =>
        advancementDateToDate(canonical)
          ? null
          : ADVANCEMENT_INVALID_DATE_MESSAGE,
    },
  ],
  [
    'time',
    {
      toEditor: asTyped,
      toCanonical: asTyped,
      reject: (canonical) =>
        TIME_PATTERN.test(canonical) ? null : ADVANCEMENT_INVALID_TIME_MESSAGE,
    },
  ],
  [
    'text',
    {
      toEditor: asTyped,
      toCanonical: (edited) => edited.trim(),
      reject: (canonical, constraints) => {
        const maxLength = constraints.maxLength ?? DEFAULT_MAX_LENGTH;
        return canonical.length > maxLength
          ? `Il valore non può superare ${maxLength} caratteri.`
          : null;
      },
    },
  ],
  [
    'currency',
    {
      toEditor: managedLeadCurrencyToDisplay,
      toCanonical: managedLeadCurrencyToCanonical,
      reject: (canonical, constraints) => {
        // The shape of an amount is stated once, beside the grouping and the
        // decimal separator it travels with.
        if (canonicalManagedLeadAmount(canonical) === null) {
          return ADVANCEMENT_INVALID_CURRENCY_MESSAGE;
        }
        const decimals = canonical.split('.')[1]?.length ?? 0;
        const maxDecimals = constraints.maxDecimals ?? DEFAULT_MAX_DECIMALS;
        return decimals > maxDecimals ||
          Number(canonical) <= (constraints.greaterThan ?? 0)
          ? ADVANCEMENT_INVALID_CURRENCY_MESSAGE
          : null;
      },
    },
  ],
]);

export const isSupportedAdvancementKind = (kind: string) =>
  KIND_ADAPTERS.has(kind);

export const hasUnsupportedAdvancementField = (
  fields: ManagedLeadAdvancementField[]
) => fields.some((field) => !isSupportedAdvancementKind(field.kind));

/**
 * The canonical value of one edited field, in the shape the update payload
 * carries. A kind with no adapter has nothing to convert with, so the value is
 * left as typed and the caller refuses the submission over it.
 */
export const canonicalAdvancementValue = (
  field: ManagedLeadAdvancementField,
  value: string
) => KIND_ADAPTERS.get(field.kind)?.toCanonical(value) ?? value;

/**
 * What the form starts from: the value HubSpot holds, and nothing else. A key
 * the detail reports as missing opens empty and the operator has to fill it in
 * before the stage that asks for it can be saved.
 */
export const initialAdvancementValues = (
  fields: ManagedLeadAdvancementField[],
  persisted: Record<string, ManagedLeadField<string>> | undefined
): ManagedLeadAdvancementValues =>
  fields.reduce<ManagedLeadAdvancementValues>((values, field) => {
    const stored = persisted?.[field.key];
    const value = stored ? getManagedLeadFieldValue(stored) : null;
    const canonical = typeof value === 'string' ? value : '';

    return {
      ...values,
      [field.key]:
        KIND_ADAPTERS.get(field.kind)?.toEditor(canonical) ?? canonical,
    };
  }, {});

/** The message one field fails on, or `null` when it is acceptable as typed. */
const advancementFieldMessage = (
  field: ManagedLeadAdvancementField,
  value: string
): string | null => {
  const adapter = KIND_ADAPTERS.get(field.kind);
  if (!adapter) return ADVANCEMENT_UNSUPPORTED_MESSAGE;

  const canonical = adapter.toCanonical(value);
  if (!canonical) {
    return field.required ? ADVANCEMENT_REQUIRED_MESSAGE : null;
  }
  return adapter.reject(canonical, field.constraints ?? {});
};

/**
 * Mirrors the constraints the policy declared, so the operator is told which
 * field to correct before the round trip. The server revalidates all of it and
 * stays the authority on what is acceptable.
 */
export const validateAdvancementValues = (
  fields: ManagedLeadAdvancementField[],
  values: ManagedLeadAdvancementValues
): ManagedLeadAdvancementErrors =>
  fields.reduce<ManagedLeadAdvancementErrors>((errors, field) => {
    const message = advancementFieldMessage(field, values[field.key] ?? '');
    return message ? { ...errors, [field.key]: message } : errors;
  }, {});

/**
 * The `fields` object of the update payload: every field the selected stage put
 * on screen, and only those. A field the form did not show is left as HubSpot
 * holds it rather than cleared, and one the policy left optional is sent only
 * when the operator actually filled it in - so nothing typed is ever dropped
 * without saying so.
 */
export const advancementPayload = (
  fields: ManagedLeadAdvancementField[],
  values: ManagedLeadAdvancementValues
): Record<string, string> | undefined => {
  const payload = fields.reduce<Record<string, string>>((entries, field) => {
    const canonical = canonicalAdvancementValue(field, values[field.key] ?? '');
    return canonical || field.required
      ? { ...entries, [field.key]: canonical }
      : entries;
  }, {});

  return Object.keys(payload).length ? payload : undefined;
};

/** Semantic key to the label the policy published, for server-side rejections. */
export const advancementFieldLabels = (
  fields: ManagedLeadAdvancementField[]
): Record<string, string> =>
  fields.reduce<Record<string, string>>(
    (labels, field) => ({ ...labels, [field.key]: field.label }),
    {}
  );
